/*
 * Dashboard structure: single source for the /admin/dashboard routes and the side menu
 *
 * id        → route segment and i18n key (dashboard.<id>.* / dashboard.<group>.<child>.*)
 * icon      → MDI icon shown in the menu
 * component → (optional) page component; DashboardPlaceholder when missing
 * noPeriod  → (optional) the page does not use the selected period, so the filter is not shown
 *
 * Hidden submenus are commented out with a `DASHBOARD-HIDDEN: <id>` marker
 */

export const DASHBOARD_BASE_PATH = '/admin/dashboard';
export const DASHBOARD_ROUTE_PREFIX = 'admin/dashboard/';
export const DASHBOARD_DEFAULT_ROUTE = DASHBOARD_ROUTE_PREFIX + 'overview';

export const DASHBOARD_MENU = [
  {
    id: 'overview',
    icon: 'mdi-view-grid-outline',
    noPeriod: true,
    component: () => import('@views/admin/dashboard/overview/Overview.vue'),
  },
  {
    id: 'users',
    icon: 'mdi-account-group',
    children: [
      {id: 'summary', component: () => import('@views/admin/dashboard/users/summary/Summary.vue')},
      {id: 'growth', component: () => import('@views/admin/dashboard/users/growth/Growth.vue')},
      {id: 'activity', component: () => import('@views/admin/dashboard/users/activity/Activity.vue')},
      {id: 'retention', component: () => import('@views/admin/dashboard/users/retention/Retention.vue')},
      {id: 'onboarding', component: () => import('@views/admin/dashboard/users/onboarding/Onboarding.vue')},
      {id: 'profile', component: () => import('@views/admin/dashboard/users/profile/Profile.vue')},
      {id: 'registration', component: () => import('@views/admin/dashboard/users/registration/Registration.vue')},
      {id: 'rankings', component: () => import('@views/admin/dashboard/users/rankings/Rankings.vue')},
    ],
  },
  {
    id: 'events',
    icon: 'mdi-calendar-multiple',
    children: [
      {id: 'summary', component: () => import('@views/admin/dashboard/events/summary/Summary.vue')},
      {id: 'evolution', component: () => import('@views/admin/dashboard/events/evolution/Evolution.vue')},
      {id: 'categories', component: () => import('@views/admin/dashboard/events/categories/Categories.vue')},
    ],
  },
  {
    id: 'usage',
    icon: 'mdi-cellphone',
    children: [
      {id: 'engagement', component: () => import('@views/admin/dashboard/usage/engagement/Engagement.vue')},
      {id: 'platforms', component: () => import('@views/admin/dashboard/usage/platforms/Platforms.vue')},
    ],
  },
  {
    id: 'moderation',
    icon: 'mdi-shield-check-outline',
    children: [{id: 'health', component: () => import('@views/admin/dashboard/moderation/health/Health.vue')}],
  },
];

function pageKeys(group, child) {
  const base = child ? `dashboard.${group.id}.${child.id}` : `dashboard.${group.id}`;
  return {
    menuKey: `${base}.menu`,
    titleKey: `${base}.title`,
    descriptionKey: `${base}.description`,
    crumbKey: child ? `dashboard.${group.id}.label` : null,
  };
}

function eachPage(callback) {
  for (const group of DASHBOARD_MENU) {
    if (!group.children) {
      callback({group, child: null, path: group.id});
      continue;
    }
    for (const child of group.children) {
      callback({group, child, path: `${group.id}/${child.id}`});
    }
  }
}

/**
 * Child routes of /admin/dashboard
 * @param {Function} placeholder - lazy component for pages without `component`
 */
export function buildDashboardRoutes(placeholder) {
  const routes = [];
  eachPage(({group, child, path}) => {
    const item = child || group;
    routes.push({
      path,
      name: DASHBOARD_ROUTE_PREFIX + path,
      component: item.component || placeholder,
      meta: {dashboard: pageKeys(group, child), noPeriod: Boolean(item.noPeriod)},
    });
  });
  return routes;
}

/**
 * Items of the Dashboard menu group in the MainLayoutBase format
 */
export function buildDashboardMenu() {
  return DASHBOARD_MENU.map((group) => {
    if (!group.children) {
      return {
        icon: group.icon,
        text: pageKeys(group, null).menuKey,
        to: `${DASHBOARD_BASE_PATH}/${group.id}`,
        routeName: DASHBOARD_ROUTE_PREFIX + group.id,
      };
    }
    return {
      icon: group.icon,
      text: `dashboard.${group.id}.label`,
      group: `${DASHBOARD_BASE_PATH}/${group.id}/`,
      // Open state; null lets VListGroup open from the route (`group` prop)
      model: null,
      children: group.children.map((child) => ({
        text: pageKeys(group, child).menuKey,
        to: `${DASHBOARD_BASE_PATH}/${group.id}/${child.id}`,
        routeName: `${DASHBOARD_ROUTE_PREFIX}${group.id}/${child.id}`,
      })),
    };
  });
}
